"use client";

import React, { useMemo, useState } from "react";
import { useAgencyRouteHistory } from "../../hooks/use-agency-routes";
import type { HistoryRoute, RoutePlanStatus } from "../../types/routes";
import { Button } from "@/app/_module/components/ui/button";
import { Input } from "@/app/_module/components/ui/input";

interface AgencyRoutesHistoryProps {
  agencyId: string;
}

const DEFAULT_LIMIT = 25;

function formatDate(value: string | null): string {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function statusBadgeClass(status: RoutePlanStatus): string {
  switch (status) {
    case "completed":
      return "bg-emerald-50 text-emerald-700 border-emerald-200";
    case "in_progress":
      return "bg-blue-50 text-blue-700 border-blue-200";
    case "planned":
      return "bg-amber-50 text-amber-700 border-amber-200";
    default:
      return "bg-gray-50 text-gray-600 border-gray-200";
  }
}

function statusLabel(status: RoutePlanStatus): string {
  switch (status) {
    case "completed":
      return "Completed";
    case "in_progress":
      return "In Progress";
    case "planned":
      return "Planned";
    default:
      return status;
  }
}

export default function AgencyRoutesHistory({
  agencyId,
}: AgencyRoutesHistoryProps) {
  const [page, setPage] = useState<number>(1);
  const [agentId, setAgentId] = useState<string>("");
  const [status, setStatus] = useState<RoutePlanStatus | "">("");
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");

  const { data, isLoading, isFetching } = useAgencyRouteHistory(agencyId, {
    page,
    limit: DEFAULT_LIMIT,
    agentId: agentId || undefined,
    status: (status || undefined) as RoutePlanStatus | undefined,
    startDate: startDate || undefined,
    endDate: endDate || undefined,
  });

  const items: HistoryRoute[] = useMemo(() => data?.items ?? [], [data]);
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / DEFAULT_LIMIT));

  // Distinct agents from the current result set for the agent filter dropdown
  const agentOptions = useMemo(() => {
    const seen = new Map<string, string>();
    for (const r of items) {
      if (!seen.has(r.userId)) seen.set(r.userId, r.agentName);
    }
    return Array.from(seen.entries()).map(([id, name]) => ({ id, name }));
  }, [items]);

  const resetToFirstPage = () => setPage(1);

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-wrap items-end gap-3 p-3 rounded-xl border border-gray-200 bg-gray-50">
        <div className="flex flex-col gap-1">
          <label className="text-xs text-gray-500">Start date</label>
          <Input
            type="date"
            value={startDate}
            onChange={(e) => {
              setStartDate(e.target.value);
              resetToFirstPage();
            }}
            className="h-9 text-sm"
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs text-gray-500">End date</label>
          <Input
            type="date"
            value={endDate}
            onChange={(e) => {
              setEndDate(e.target.value);
              resetToFirstPage();
            }}
            className="h-9 text-sm"
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs text-gray-500">Agent</label>
          <select
            value={agentId}
            onChange={(e) => {
              setAgentId(e.target.value);
              resetToFirstPage();
            }}
            className="h-9 text-sm rounded-md border border-gray-200 bg-white px-2"
          >
            <option value="">All agents</option>
            {agentOptions.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </select>
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs text-gray-500">Status</label>
          <select
            value={status}
            onChange={(e) => {
              setStatus(e.target.value as RoutePlanStatus | "");
              resetToFirstPage();
            }}
            className="h-9 text-sm rounded-md border border-gray-200 bg-white px-2"
          >
            <option value="">All</option>
            <option value="in_progress">In Progress</option>
            <option value="completed">Completed</option>
            <option value="planned">Planned</option>
          </select>
        </div>
        {(startDate || endDate || agentId || status) && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setStartDate("");
              setEndDate("");
              setAgentId("");
              setStatus("");
              resetToFirstPage();
            }}
          >
            Clear
          </Button>
        )}
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-xl border border-gray-200">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-500">
            <tr>
              <th className="text-left font-medium px-4 py-2.5">Agent</th>
              <th className="text-left font-medium px-4 py-2.5">Route</th>
              <th className="text-left font-medium px-4 py-2.5">Status</th>
              <th className="text-left font-medium px-4 py-2.5">Started</th>
              <th className="text-left font-medium px-4 py-2.5">Completed</th>
              <th className="text-left font-medium px-4 py-2.5">Stops</th>
              <th className="text-left font-medium px-4 py-2.5">Doors</th>
              <th className="text-left font-medium px-4 py-2.5">Miles</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td
                  colSpan={8}
                  className="px-4 py-10 text-center text-gray-500"
                >
                  Loading...
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td
                  colSpan={8}
                  className="px-4 py-10 text-center text-gray-500"
                >
                  No routes match the current filters.
                </td>
              </tr>
            ) : (
              items.map((route) => (
                <tr
                  key={route.id}
                  className="border-t border-gray-100 hover:bg-gray-50"
                >
                  <td className="px-4 py-3 font-medium text-gray-900">
                    {route.agentName}
                  </td>
                  <td className="px-4 py-3 text-gray-700 truncate max-w-[220px]">
                    {route.routeName || "—"}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs border ${statusBadgeClass(
                        route.status,
                      )}`}
                    >
                      {statusLabel(route.status)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-600">
                    {formatDate(route.startedAt)}
                  </td>
                  <td className="px-4 py-3 text-gray-600">
                    {formatDate(route.completedAt)}
                  </td>
                  <td className="px-4 py-3 text-gray-700">
                    {route.stopsVisited}/{route.totalStops}
                    {route.stopsSkipped > 0 || route.stopsNotHome > 0 ? (
                      <span className="text-gray-400 text-xs ml-1">
                        ({route.stopsSkipped} skipped
                        {route.stopsNotHome > 0
                          ? `, ${route.stopsNotHome} not home`
                          : ""}
                        )
                      </span>
                    ) : null}
                  </td>
                  <td className="px-4 py-3 text-gray-700">
                    {route.doorsKnocked}
                  </td>
                  <td className="px-4 py-3 text-gray-700">
                    {route.totalMilesDriven
                      ? route.totalMilesDriven.toFixed(1)
                      : "—"}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between">
        <div className="text-xs text-gray-500">
          {total === 0
            ? "0 routes"
            : `Showing ${(page - 1) * DEFAULT_LIMIT + 1}–${Math.min(
                page * DEFAULT_LIMIT,
                total,
              )} of ${total}`}
          {isFetching && !isLoading ? " · refreshing…" : ""}
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={page <= 1 || isLoading}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            Previous
          </Button>
          <span className="text-xs text-gray-500">
            Page {page} of {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= totalPages || isLoading}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  );
}
